/**
 * CbamFormResume.js
 *
 * Sidebar's "CBAM Form" link goes to /cbam-form which renders this component.
 * It does NOT show UI — it's purely a routing controller:
 *
 *   1. Read auth state from AuthContext.
 *   2. Only AppAdmin and AppUser may use this. Reject SuperAdmin and Verifier.
 *   3. Query Firestore for the most recent in-progress audit in the user's tenant.
 *   4. If found → <Navigate to="/form-a/{auditId}" />
 *   5. If not   → create a new audit doc (with tenantId on parent) and navigate.
 *
 * "In progress" = status != 'completed'. Sorted by updatedAt desc, limit 1.
 *
 * NOTE: This effect uses a useRef guard so it runs at most once per mount,
 * even under React.StrictMode (which intentionally double-invokes effects in
 * development to surface bugs). Without the guard we'd race-create duplicate
 * audits.
 *
 * NOTE: Firestore will require a composite index the first time this runs.
 * The error in the console will include a link — click it, wait 1–2 min for
 * the index to build, and try again.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import {
  collection, query, where, orderBy, limit, getDocs, doc, setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore as db } from './firebase.config';
import { useAuth } from '../auth/AuthContext';

const CbamFormResume = () => {
  const [targetAuditId, setTargetAuditId] = useState(null);
  const [error, setError] = useState(null);

  // StrictMode-safe single-fire guard. useRef survives across renders, but is
  // re-created on a true unmount/remount. So this allows real navigation back
  // to /cbam-form to work, while blocking the StrictMode double-invoke.
  const hasRun = useRef(false);

  const {
  currentUser,
  isLoggedIn,
  isSuperAdmin,
  isAppAdmin,
  isAppUser,
  tenantId,
  tenantName,
  loading: authLoading,
} = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (hasRun.current) return;     // <-- StrictMode-safe guard

    if (!isLoggedIn) {
      setError('You are not logged in.');
      return;
    }

    if (!isSuperAdmin && !isAppAdmin && !isAppUser) {
      setError('You do not have permission to access CBAM forms.');
      return;
    }

    if (!isSuperAdmin && !tenantId) {
      setError('No tenant assigned to your account. Please contact your administrator.');
      return;
    }

    hasRun.current = true;          // <-- mark as started; second invoke skips above

    const userEmail = currentUser?.email || '';

    const findOrCreateAudit = async () => {
      try {
        const q = query(
          collection(db, 'auditReports'),
          where('tenantId', '==', tenantId),
          where('status', '!=', 'completed'),
          orderBy('status'),
          orderBy('updatedAt', 'desc'),
          limit(1)
        );

        const snap = await getDocs(q);

        if (!snap.empty) {
          setTargetAuditId(snap.docs[0].id);
          return;
        }

        // None found — create a fresh audit. Parent doc carries metadata.
        const newId = crypto.randomUUID();
        await setDoc(doc(db, 'auditReports', newId), {
          tenantId,
          tenantName,
          createdBy: userEmail,
          createdAt: serverTimestamp(),
          updatedBy: userEmail,
          updatedAt: serverTimestamp(),
          status: 'draft',
        });
        setTargetAuditId(newId);
      } catch (err) {
        console.error('CbamFormResume error:', err);
        // Reset the guard so a retry (next click) can run again.
        hasRun.current = false;
        setError(
          'Could not start CBAM form. ' +
          (err?.message || 'Try refreshing the page.')
        );
      }
    };

    findOrCreateAudit();
  }, [authLoading, isLoggedIn, isAppAdmin, isAppUser, tenantId, tenantName, currentUser]);

  // ── Render guards ──────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!targetAuditId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading your CBAM form...
        </Typography>
      </Box>
    );
  }

  return <Navigate to={`/form-a/${targetAuditId}`} replace />;
};

export default CbamFormResume;